import React from 'react';
import { Shield, ChevronLeft } from 'lucide-react';

export const PrivacyView: React.FC = () => {
    return (
        <div className="min-h-screen bg-[#0a0f18] text-slate-300 font-sans p-6 md:p-12 selection:bg-orange-500/30">
            <div className="max-w-3xl mx-auto">
                
                <a href="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-8 group">
                    <div className="bg-slate-800 p-2 rounded-full group-hover:bg-slate-700 transition-colors">
                        <ChevronLeft className="w-4 h-4" />
                    </div>
                    <span className="font-medium text-sm">Voltar ao aplicativo</span>
                </a>

                <div className="mb-12">
                    <div className="inline-flex items-center gap-2 bg-orange-500/10 text-orange-500 px-3 py-1 rounded-full text-xs font-bold mb-6 border border-orange-500/20">
                        <Shield className="w-4 h-4" /> Documento Legal
                    </div>
                    <h1 className="text-3xl md:text-5xl font-black text-white mb-4 tracking-tight">Privacidade e Termos</h1>
                    <p className="text-slate-400">Última atualização: Abril de 2026</p>
                </div>

                <div className="space-y-12">
                    {/* PRIVACY POLICY */}
                    <section className="bg-slate-900/50 outline outline-1 outline-slate-800 rounded-3xl p-6 md:p-10 shadow-2xl relative overflow-hidden text-sm md:text-base leading-relaxed">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/5 rounded-full blur-3xl"></div>
                        <div className="absolute bottom-0 left-0 w-32 h-32 bg-orange-500/5 rounded-full blur-3xl"></div>
                        
                        <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                            <span className="bg-blue-600 w-2 h-6 rounded-full inline-block"></span>
                            Política de Privacidade e Proteção de Dados
                        </h2>
                        
                        <p className="mb-8">
                            O Driver Score (<a href="https://driverscore.space" className="text-blue-400 hover:text-blue-300 transition-colors">driverscore.space</a>), adiante denominado apenas como "Plataforma", estabelece a presente política em conformidade com a Lei nº 13.709/2018 (LGPD) para informar como os dados dos usuários são tratados.
                        </p>

                        <div className="space-y-8">
                            <div>
                                <h3 className="text-lg font-bold text-slate-200 mb-3">1. Coleta de Dados e Finalidade</h3>
                                <ul className="list-disc pl-5 space-y-3 text-slate-400">
                                    <li><strong className="text-slate-300">Identificação Pessoal:</strong> Coletamos nome e e-mail via autenticação Google para criação de perfil único e segurança no acesso.</li>
                                    <li><strong className="text-slate-300">Dados de Veículos:</strong> A inserção de placas (ex: UDE 5J87) visa a organização individual do dashboard de ganhos e identificação na frota.</li>
                                    <li><strong className="text-slate-300">Geolocalização:</strong> A localização via GPS é coletada apenas mediante acionamento voluntário da função "Mapear Localização", com o objetivo exclusivo de registrar pontos de interesse e posicionamento de frota. <strong>Não realizamos rastreio em segundo plano.</strong></li>
                                </ul>
                            </div>

                            <div>
                                <h3 className="text-lg font-bold text-slate-200 mb-3">2. Armazenamento e Segurança</h3>
                                <p className="text-slate-400">
                                    Os dados são armazenados em infraestrutura de nuvem segura com protocolos de criptografia de ponta. O acesso aos dados é restrito ao titular da conta e os tokens gerados durante o login e sessões não são visualizáveis de forma ampla pela equipe.
                                </p>
                            </div>

                            <div>
                                <h3 className="text-lg font-bold text-slate-200 mb-3">3. Compartilhamento de Dados</h3>
                                <p className="text-slate-400">
                                    A Plataforma não comercializa, cede ou compartilha dados pessoais com terceiros para fins publicitários. Dados agregados e anônimos podem ser utilizados para estatísticas internas de desempenho da frota e correções de rotas a fim de melhorar a conversão da frota global e evitar problemas para a comunidade.
                                </p>
                            </div>

                            <div>
                                <h3 className="text-lg font-bold text-slate-200 mb-3">4. Direitos do Usuário</h3>
                                <p className="mb-3 text-slate-400">Em conformidade com a LGPD, o usuário possui o direito de:</p>
                                <ul className="list-disc pl-5 space-y-2 text-slate-400">
                                    <li>Acessar, retificar ou excluir seus dados do sistema a qualquer momento pelo app.</li>
                                    <li>Revogar o consentimento de uso da plataforma, implicando na desativação da conta instantaneamente sem taxas extras.</li>
                                </ul>
                            </div>
                        </div>
                    </section>

                    {/* TERMS OF USE */}
                    <section className="bg-slate-900/50 outline outline-1 outline-slate-800 rounded-3xl p-6 md:p-10 shadow-2xl relative overflow-hidden text-sm md:text-base leading-relaxed">
                        
                        <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                            <span className="bg-orange-600 w-2 h-6 rounded-full inline-block"></span>
                            Termos e Condições de Uso
                        </h2>

                        <div className="space-y-8">
                            <div>
                                <h3 className="text-lg font-bold text-slate-200 mb-3">1. Objeto</h3>
                                <p className="text-slate-400">
                                    O Driver Score é uma ferramenta de suporte à gestão de ganhos e localização para motoristas independentes. A Plataforma não possui vínculo empregatício com os usuários ou com as empresas de transporte mencionadas no aplicativo. 
                                </p>
                            </div>

                            <div>
                                <h3 className="text-lg font-bold text-slate-200 mb-3">2. Responsabilidade do Usuário</h3>
                                <p className="text-slate-400">
                                    O usuário compromete-se a inserir dados verídicos e a utilizar a plataforma de forma ética, sendo estritamente vedado o uso do sistema para atividades ilícitas, disseminação de fake news contra colegas de ruas ou uso massivo que viole a privacidade de outros profissionais da frota, sob pena de encerramento da conta do software.
                                </p>
                            </div>

                            <div>
                                <h3 className="text-lg font-bold text-slate-200 mb-3">3. Limitação de Responsabilidade</h3>
                                <p className="text-slate-400">
                                    O Driver Score busca a máxima precisão nos cálculos e atualizações logísticas de mapas, porém, não se responsabiliza por eventuais divergências de valores financeiros, relatórios não consolidados pelos players que o motorista possua e oscilações de sinal de GPS decorrentes de falhas de hardware ou operadoras de telefonia locais do usuário.
                                </p>
                            </div>

                            <div>
                                <h3 className="text-lg font-bold text-slate-200 mb-3">4. Foro</h3>
                                <p className="text-slate-400">
                                    Para dirimir quaisquer controvérsias oriundas deste termo, as partes elegem o foro da Comarca de São Paulo, SP com exclusão a qualquer outro sem contiguidade.
                                </p>
                            </div>
                        </div>
                    </section>
                </div>

                <div className="mt-12 text-center text-slate-500 text-xs border-t border-slate-800 pt-8 pb-4">
                    <p>&copy; {new Date().getFullYear()} Driver Score. Todos os direitos reservados.</p>
                </div>

            </div>
        </div>
    );
};
